import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, switchMap } from 'rxjs';
import { AuthService, endsTheSession } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { isImpersonationErrorDetail } from '@/app/features/impersonation/models/impersonation.models';

export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const notification = inject(NotificationService);

  const token = authService.getToken();

  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // End the session when it can't be recovered: clear it, tell the user, and
  // send them to sign-in. Guarded by token presence so concurrent 401s don't
  // stack toasts or redirects — the first call clears the token, the rest are
  // no-ops.
  const endExpiredSession = (): void => {
    if (authService.expireSession(notification)) {
      router.navigate(['/sign-in'], {
        queryParams: { returnUrl: router.url },
      });
    }
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Don't attempt token refresh for impersonation-scoped 401s — the token
      // is fine, the impersonation session itself expired/was revoked. The
      // impersonation interceptor handles cleanup and redirection.
      const detail = (error.error as { detail?: unknown })?.detail;
      if (isImpersonationErrorDetail(detail)) {
        return throwError(() => error);
      }

      // A 401 on an auth endpoint (sign-in, /auth/refresh, /auth/me, ...)
      // cannot be solved by refreshing. Let the caller handle it: the sign-in
      // form shows its own error, and a failed /auth/refresh surfaces here as
      // the refresh error handled below.
      if (req.url.includes('/auth/')) {
        return throwError(() => error);
      }

      // Non-auth 401: refresh the token (single-flight) and retry once.
      return authService.refreshAccessToken().pipe(
        catchError((refreshError: unknown) => {
          // A refresh the server could not answer (no network, a 5xx) says nothing about the
          // session: it stays, and the caller sees THAT failure, not a «session expired».
          if (!endsTheSession(refreshError)) {
            return throwError(() => refreshError);
          }
          // The server refused the refresh — the session is unrecoverable.
          endExpiredSession();
          return throwError(() => error);
        }),
        switchMap((refreshResult) => {
          const retryReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshResult.access_token}`,
            },
          });

          return next(retryReq).pipe(
            catchError((retryError: HttpErrorResponse) => {
              // Refreshed but still rejected — don't loop, end the session.
              if (retryError.status === 401) {
                endExpiredSession();
              }
              return throwError(() => retryError);
            }),
          );
        }),
      );
    }),
  );
};
